import toast from "react-hot-toast";
import i18next from "i18next";
import * as Sentry from "@sentry/react";
import {transformDataToFormData} from "../../Utils/HttpHelper";
import {BaseClient, BaseUrlClient} from "../../Api/HttpClient";
import axios from "axios";

const identifyUserInSentry = (user) => {
    if (!user?.id) {
        Sentry.setUser(null);
        return;
    }

    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.email,
        type: user.type,
        roles: user.roles,
    });
};

export const createAuthSlice = (set, get) => ({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: null,
    userRoles: ['ROLE_ADMIN', 'ROLE_CUSTOMER', 'ROLE_FREELANCER'],

    login: async (username, password) => {
        try {
            const response = await BaseUrlClient().post('oauth/token', {
                username: username,
                password: password,
                grant_type: 'password',
                client_id: import.meta.env.VITE_APP_CLIENT_ID,
                client_secret: import.meta.env.VITE_APP_CLIENT_SECRET
            });

            if (response.status !== 200) {
                return;
            }

            set({
                isAuthenticated: true,
                accessToken: response.data.access_token,
                refreshToken: response.data.refresh_token,
                user: null,
            });
        } catch (error) {
            if (error?.response?.data?.message === 'The user credentials were incorrect.') {
                toast.error(i18next.t('login.toast_credentials_incorrect'));
                return;
            }

            toast.error(error?.response?.data?.message);
            return;
        }

        try {
            await get().getMe();
        } catch (error) {
            get().logOut();
            toast.error('Kon jouw profiel niet ophalen. Log opnieuw in.');
        }
    },
    getMe: async () => {
        const response = await BaseClient().get('/me');
        if (response.status === 200) {
            set({user: response.data});
            identifyUserInSentry(response.data);
            const userLocale = response.data?.locale ?? response.data?.data?.locale;
            if (userLocale && ['nl','en','fr'].includes(userLocale) && i18next.language !== userLocale) {
                i18next.changeLanguage(userLocale);
            }
        } else {
            toast.error(response.data);
        }
    },
    updateMyLocale: async (locale) => {
        try {
            const response = await BaseClient().patch('/me/locale', {locale});
            if (response.status === 200) {
                set({user: response.data?.data ?? response.data});
                if (i18next.language !== locale) {
                    i18next.changeLanguage(locale);
                }
                toast.success(i18next.t('language.toast_updated'));
                return true;
            }
            return false;
        } catch (error) {
            toast.error(i18next.t('register_page.generic_error'));
            return false;
        }
    },
    updateMe: async (data) => {
        await BaseClient().post('/me',transformDataToFormData(data))
            .then(response => {
                if (response.status === 200) {
                    set({user: response.data})
                } else {
                    toast.error(response.data);
                }
            })
    },
    registerFreelancer: async (data) => {
        const dataToSubmit = transformDataToFormData(data);

        return await axios
            .post(`${import.meta.env.VITE_APP_API_URL}/register/freelancer`, dataToSubmit, {
                'Content-Type': 'multipart/form-data'
            })
            .then((response) => {
                if (response.status === 200) {
                    set({user: response.data});
                    get().getMe()
                }

                return null;
            })
            .catch((error) => {
                throw error;
                console.error('Error creating freelancer', error);
            });
    },
    checkEmailAvailable: async (email) => {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_APP_API_URL}/register/check-email`,
                {params: {email}}
            );
            return response.data?.available === true;
        } catch (error) {
            return true;
        }
    },
    registerCustomer: async (data) => {
        const dataToSubmit = transformDataToFormData(data);

        return await BaseClient()
            .post('/register/customer', dataToSubmit)
            .then((response) => {
                if (response.status === 201) {
                    set({user: response.data});
                    get().getMe()
                }
                return response
            })
            .catch((error) => {
                console.error('Error creating customer', error);
                throw error;
            });
    },
    logOut: () => {
        set({isAuthenticated: false})
        set({accessToken: null})
        set({refreshToken: null})
        set({user: null})
        Sentry.setUser(null);
    },
    forgotPassword: async (email) => {
        return await BaseClient().post('/forgot-password', {
            email: email
        }).then(response => {
            if (response.status === 200) {
                return true;
            } else {
                toast.error(response.data);
                return false;
            }
        })
    },
    resetPassword: async (data) => {
        return await BaseClient().post('/reset-password', data)
            .then(response => {
                return response.data;
            }).catch(error => {
                return error.response.data;
            });
    }
})